import React, { useCallback, useState } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { ChatMessageItem } from "@aidkitorg/types/lib/convoai";
import { Button, useToast, Icon, nonHookMarkdown } from "@aidkitorg/component-library";
import { usePost } from "../../API";

function getInstructionSummary(instruction: survey.Instruction): { title: string; subtitle?: string } {
  switch (instruction.kind) {
    case 'Text Instruction': {
      const content = instruction.content || '';
      const preview = content.length > 60 ? content.slice(0, 60) + '...' : content;
      return {
        title: instruction.role || 'system',
        subtitle: preview
      };
    }
    case 'Conversation Part':
      return { title: 'Conversation', subtitle: instruction.part };
    case 'Example Images':
      return { title: 'Example Images', subtitle: `${instruction.imageUrls.length} image(s)` };
    case 'Message Template Reference':
      return {
        title: 'Template Reference',
        subtitle: `${instruction.templateRef.name}${instruction.templateRef.version !== undefined ? ` v${instruction.templateRef.version}` : ''}`
      };
    default:
      return { title: 'Unknown' };
  }
}

function CollapsibleInstruction({ instruction, index, defaultExpanded = false }: {
  instruction: survey.Instruction;
  index: number;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const summary = getInstructionSummary(instruction);

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <Icon
          name="ChevronRightIcon"
          className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
        <span className="text-xs text-gray-400 font-mono">{index + 1}</span>
        <span className="text-xs font-medium text-gray-600 uppercase">{summary.title}</span>
        {summary.subtitle && (
          <span className="text-xs text-gray-500 truncate flex-1">{summary.subtitle}</span>
        )}
      </button>
      {isExpanded && (
        <div className="p-2">
          <InstructionContent instruction={instruction} />
        </div>
      )}
    </div>
  );
}

function InstructionContent({ instruction }: { instruction: survey.Instruction }) {
  switch (instruction.kind) {
    case 'Text Instruction':
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              {instruction.role || 'system'}
            </span>
            {instruction.tag && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                {instruction.tag}
              </span>
            )}
          </div>
          <div className="prose prose-sm max-w-none text-gray-700">
            {nonHookMarkdown(instruction.content)}
          </div>
        </div>
      );
    case 'Conversation Part':
      return (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
          <div className="flex items-center gap-2">
            <Icon name="ChatBubbleLeftRightIcon" className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">
              Conversation: <code className="bg-purple-100 px-1 rounded">{instruction.part}</code>
            </span>
          </div>
        </div>
      );
    case 'Example Images':
      return (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="PhotoIcon" className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Example Images</span>
          </div>
          {instruction.description && (
            <p className="text-xs text-green-600 mb-2">{instruction.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            {instruction.imageUrls.map((url, i) => (
              <div key={i} className="relative">
                <img
                  src={url}
                  alt={`Example ${i + 1}`}
                  className="w-16 h-16 object-cover rounded border border-green-300"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    case 'Message Template Reference':
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="flex items-center gap-2">
            <Icon name="DocumentDuplicateIcon" className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">
              Template Reference: <code className="bg-amber-100 px-1 rounded">{instruction.templateRef.name}</code>
              {instruction.templateRef.version !== undefined && (
                <span className="text-xs text-amber-500 ml-1">v{instruction.templateRef.version}</span>
              )}
            </span>
          </div>
        </div>
      );
    default:
      return null;
  }
}

function OutputFormatPreview({ output }: { output: survey.OutputFormat }) {
  if (output.type === 'template') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
        <div className="flex items-center gap-2">
          <Icon name="DocumentDuplicateIcon" className="w-4 h-4 text-amber-600" />
          <span className="text-sm font-medium text-amber-700">
            Output from template: <code className="bg-amber-100 px-1 rounded">{output.templateRef.name}</code>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
      <pre className="text-xs text-slate-700 overflow-auto">
        {JSON.stringify(formatOutputSchema(output), null, 2)}
      </pre>
    </div>
  );
}

function formatOutputSchema(output: survey.ObjectDef): object {
  const schema: Record<string, unknown> = {};
  for (const field of output.fields || []) {
    if (typeof field.type === 'string') {
      schema[field.name] = field.type;
    } else if (field.type.type === 'string') {
      schema[field.name] = 'string' + (field.type.format ? ` (format: ${field.type.format})` : '');
    } else if (field.type.type === 'enum') {
      schema[field.name] = `enum(${field.type.values.join(' | ')})`;
    } else if (field.type.type === 'array') {
      schema[field.name] = `array<${JSON.stringify(field.type.items)}>`;
    } else if (field.type.type === 'object') {
      schema[field.name] = formatOutputSchema(field.type);
    }
  }
  return schema;
}

function chatItemsToMarkdown(items: ChatMessageItem[]): string {
  const lines: string[] = [];

  for (const item of items) {
    if (item.type === 'message') {
      for (const part of item.content) {
        if (part.type === 'text') {
          lines.push(part.content);
        } else if (part.type === 'image') {
          lines.push(`![image](${part.url})`);
        }
      }
      lines.push('');
    }
  }

  return lines.join('\n');
}

export function MessageTemplatePreview({ template }: { template: survey.EditableMessageTemplate }) {
  const { toast } = useToast();
  const previewRequest = usePost('/program/admin/prompts/preview');
  const [isCopying, setIsCopying] = useState(false);

  const handleCopy = useCallback(async () => {
    if (template.instructions.length === 0) {
      toast({
        description: 'No instructions to copy',
        autoClose: true,
        duration: 1_500
      });
      return;
    }

    setIsCopying(true);
    try {
      const { items } = await previewRequest({ item: template });
      const markdown = chatItemsToMarkdown(items || []);
      await navigator.clipboard.writeText(markdown);
      toast({
        description: 'Copied template as markdown',
        autoClose: true,
        duration: 1_500
      });
    } catch (e) {
      toast({
        description: 'Failed to copy template',
        autoClose: true,
        duration: 1_500
      });
    } finally {
      setIsCopying(false);
    }
  }, [template, toast, previewRequest]);

  return (
    <div className="space-y-4">
      <div className="border-b border-gray-200 pb-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {template.name || <span className="text-gray-400 italic">Untitled Template</span>}
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            disabled={isCopying}
            icon={isCopying ? <Icon name="ArrowPathIcon" className="animate-spin" /> : <Icon name="ClipboardDocumentIcon" />}
          >
            {isCopying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
          <span>Version {template.version || 0}</span>
        </div>
      </div>

      {template.description && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
          <div className="prose prose-sm max-w-none text-gray-600 bg-gray-50 rounded-md p-3">
            {nonHookMarkdown(template.description)}
          </div>
        </div>
      )}

      {template.templateVariables && template.templateVariables.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Template Variables</h4>
          <div className="space-y-2">
            {template.templateVariables.map((variable, i) => (
              <div key={i} className="bg-cyan-50 border border-cyan-200 rounded-md p-2 text-sm">
                <code className="font-mono text-cyan-700">{variable.key}</code>
                <span className="text-gray-500 mx-2">=</span>
                <span className="text-gray-700">{variable.value}</span>
                {variable.description && (
                  <p className="text-xs text-gray-500 mt-1">{variable.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {template.agents && template.agents.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Delegated Agents</h4>
          <p className="text-xs text-gray-500 mb-2">
            Run before the main coach response. Reference outputs in Text Instructions as{' '}
            <code className="bg-gray-100 px-1 rounded">{'{agent_output:name}'}</code>.
          </p>
          <div className="space-y-2">
            {template.agents.map((agent, i) => (
              <div key={i} className="bg-violet-50 border border-violet-200 rounded-md p-2 text-sm">
                <div className="font-mono text-violet-800">{agent.name}</div>
                <div className="text-gray-600 mt-1">
                  Template <span className="font-mono">{agent.messageTemplate.name}</span>
                  <span className="text-gray-500"> v{agent.messageTemplate.version}</span>
                  {agent.model?.name && (
                    <span className="text-gray-500"> · model {agent.model.name}</span>
                  )}
                  {agent.timeoutMs != null && (
                    <span className="text-gray-500"> · timeout {agent.timeoutMs}ms</span>
                  )}
                  {agent.maxAttempts != null && (
                    <span className="text-gray-500"> · attempts {agent.maxAttempts}</span>
                  )}
                </div>
                {agent.outputBinding && (
                  <div className="text-xs text-gray-500 mt-1">
                    Output binding: <code className="bg-violet-100 px-1 rounded">{agent.outputBinding}</code>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">
          Instructions ({template.instructions.length})
        </h4>
        {template.instructions.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No instructions defined</p>
        ) : (
          <div className="space-y-2">
            {template.instructions.map((instruction, i) => (
              <CollapsibleInstruction key={i} instruction={instruction} index={i} />
            ))}
          </div>
        )}
      </div>

      {template.output && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Output Format</h4>
          <OutputFormatPreview output={template.output} />
        </div>
      )}
    </div>
  );
}
